import Phaser from "phaser";
import { useRealAssets } from "../config";
import { ConstructionAssets, allAssetKeys, type VisualKey } from "./AssetManifest";
import * as P from "../art/props";

/**
 * FÁBRICA VISUAL: convierte una CLAVE VISUAL en un GameObject.
 *
 *   asset real disponible  → se renderiza el sprite (Image, origen en los pies)
 *   asset ausente          → se dibuja el placeholder vectorial (art/props.ts)
 *
 * Así el arte se sustituye de forma progresiva sin romper nunca el juego
 * (Fase 4). La lógica del juego no llama aquí directamente: lo hace la escena
 * al construir el decorado y los interactivos.
 */

/** Clave de textura de Phaser para un asset del manifest. */
export function textureKeyFor(key: VisualKey): string {
  return `asset:${key}`;
}

/** Encola en el preload de la escena los assets reales (si están activados). */
export function preloadConstructionAssets(scene: Phaser.Scene): void {
  if (!useRealAssets()) return;
  for (const key of allAssetKeys()) {
    const def = ConstructionAssets[key];
    if (!def.available) continue; // solo los que ya existen (evita 404 masivos)
    scene.load.image(textureKeyFor(key), def.path);
  }
  // Si un asset falta, NO se rompe: se marcará ausente y se usará el fallback.
  scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
    /* silencioso: el placeholder cubre el hueco */
  });
}

/** ¿Se va a renderizar el sprite real (true) o el placeholder (false)? */
export function willUseSprite(scene: Phaser.Scene, key: VisualKey): boolean {
  return useRealAssets() && scene.textures.exists(textureKeyFor(key));
}

/**
 * Construye el objeto visual para una clave. Devuelve SIEMPRE un Container
 * cuyo origen (0,0) es la base-centro (los pies), listo para colocarse en
 * iso(x,y) con su depth. Nunca devuelve null.
 */
export function makeVisual(scene: Phaser.Scene, key: VisualKey): Phaser.GameObjects.Container {
  const def = ConstructionAssets[key];

  if (def && willUseSprite(scene, key)) {
    const img = scene.add
      .image(0, 0, textureKeyFor(key))
      .setOrigin(def.originX ?? 0.5, def.originY ?? 1);
    if (def.scale) img.setScale(def.scale);
    return scene.add.container(0, 0, [img]);
  }

  // --- placeholder vectorial ---
  return fallbackVisual(scene, def?.fallback ?? key);
}

/** Traduce el nombre de placeholder a la función de art/props.ts. */
function fallbackVisual(scene: Phaser.Scene, name: string): Phaser.GameObjects.Container {
  switch (name) {
    case "epiPlayer":
      return P.worker(scene, { shirt: 0xdf5f2a, vest: 0xdf5f2a, hair: 0x5a4632 });
    case "workerAntonio":
      return P.worker(scene, { shirt: 0xb06a3a, hair: 0x5a3a24 });
    case "workerLucia":
      return P.worker(scene, { shirt: 0x2f7d78, vest: 0xe6d020, hair: 0x3a2a20 });
    case "workerManolo":
      return P.worker(scene, { shirt: 0x5a5f68, helmet: 0xf0c400, hair: 0x8a8a90 });
    case "helmet": return P.helmet(scene);
    case "locker": return P.locker(scene);
    case "cone": return P.cone(scene);
    case "fence": return P.fence(scene);
    case "warningSign": return P.warningSign(scene, "generic");
    case "warningFall": return P.warningSign(scene, "fall");
    case "extinguisher": return P.extinguisher(scene);
    case "mixer": return P.mixer(scene);
    case "wheelbarrow": return P.wheelbarrow(scene);
    case "sacks": return P.sacks(scene);
    case "pipes": return P.pipes(scene);
    case "pallet": return P.pallet(scene);
    case "crate": return P.crate(scene);
    case "crateStack": return P.crateStack(scene);
    case "barrel": return P.barrel(scene);
    case "scaffold": return P.scaffold(scene, 2);
    case "ladder": return P.ladder(scene);
    case "hut": return P.hut(scene);
    case "fallenTool": return P.fallenTool(scene);
    default:
      // último recurso: un marcador neutro, nunca null
      return scene.add.container(0, 0, [P.shadow(scene, 22, 11), scene.add.rectangle(0, -14, 24, 28, 0x8a785a).setOrigin(0.5, 1)]);
  }
}
